import { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { CalendarBlank, Clock, MapPin, Users } from "@phosphor-icons/react";
import { useFetch } from "@/lib/useFetch";
import { fmtDate, fmtTime } from "@/lib/api";
import { Agenda } from "@/components/Agenda";
import { PageHero, Section, Loader } from "@/components/Blocks";
import { RiskNotice } from "@/components/layout/PublicLayout";
import { WaitlistForm } from "@/pages/public/Cities";
import { PriceCards } from "@/components/PriceCards";

const ClosedPanel = ({ e }) => (
  <div className="panel space-y-4 p-6" data-testid="registration-closed">
    <div className="eyebrow">{e.registration?.season_name}</div>
    <p className="font-display text-2xl font-bold uppercase">Coming soon – registration closed</p>
    <span className="inline-block rounded-full bg-[#E2E8F0] px-3 py-1 text-xs font-bold uppercase text-[#475569]" data-testid="registration-opens-label">
      {e.registration?.opens_at ? `Registration opens ${fmtDate(e.registration.opens_at, e.city?.timezone)}` : "Registration opens soon"}
    </span>
    <PriceCards compact tiers={e.price_tiers} tz={e.city?.timezone} />
    <WaitlistForm citySlug={e.city?.slug} eventId={e.id} season={e.season} cta="registration-open" title="Notify me when registration opens" />
  </div>
);

export default function EventDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { data: e, loading, error } = useFetch(`/events/${slug}`);
  const [tier, setTier] = useState(null);
  if (loading) return <Loader />;
  if (error) return <Section><p data-testid="event-error">{error}</p></Section>;
  const tz = e.city?.timezone;
  const tiers = (e.price_tiers || []).filter((t) => t.on_sale);
  const selected = tiers.some((t) => t.code === tier) ? tier : tiers[0]?.code;
  const taxLabel = (e.tax_profile?.components || []).filter((c) => c.enabled !== false).map((c) => `${c.label} ${c.rate_bp / 100}%`).join(" + ");
  const ld = { "@context": "https://schema.org", "@type": "Event", name: e.title, startDate: e.start_at, endDate: e.end_at, eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode", description: e.description, organizer: { "@type": "Organization", name: "ForeXperts AI Inc." },
    location: { "@type": "Place", name: e.venue?.name || "Venue announced soon", address: e.venue?.address || `${e.city?.name}, ${e.city?.province}, Canada` },
    ...(tiers.length ? { offers: tiers.map((t) => ({ "@type": "Offer", name: t.name, price: (t.price_cents / 100).toFixed(2), priceCurrency: "USD", availability: e.seats_left > 0 ? "https://schema.org/InStock" : "https://schema.org/SoldOut", validThrough: t.ends_at })) } : {}) };
  return (
    <>
      <script type="application/ld+json">{JSON.stringify(ld)}</script>
      <PageHero eyebrow={`${e.city?.name}, ${e.city?.province}`} title={e.title} sub={e.description}>
        <div className="mt-6 flex flex-wrap gap-5 text-sm text-white/80">
          <span className="flex items-center gap-2"><CalendarBlank size={18} className="text-[#E0A21B]" />{fmtDate(e.start_at, tz, { weekday: "short" })}{e.end_at.slice(0, 10) !== e.start_at.slice(0, 10) && !e.agenda ? ` – ${fmtDate(e.end_at, tz)}` : ""}</span>
          <span className="flex items-center gap-2"><MapPin size={18} className="text-[#E0A21B]" /><span data-testid="event-venue">{e.venue ? `${e.venue.name}, ${e.venue.address}` : "Venue being confirmed. We'll email it to you as soon as it's booked."}</span></span>
          <span className="flex items-center gap-2" data-testid="event-seats-left"><Users size={18} className="text-[#E0A21B]" /><span className="font-mono-num">{e.seats_left}</span> of {e.capacity} seats left</span>
        </div>
      </PageHero>
      <Section>
        <div className="grid gap-8 lg:grid-cols-12">
          <div className="space-y-6 lg:col-span-7">
            <div className="panel p-6">
              <div className="eyebrow">Run of show</div>
              <div className="mt-4">
                {e.agenda?.length ? <Agenda agenda={e.agenda} doorsOpen={e.doors_open} tz={tz} /> : <div className="divide-y">{e.sessions.map((s) => (
                  <div key={s.title} className="flex flex-wrap items-center justify-between gap-2 py-3">
                    <span className="font-semibold">{s.title}</span>
                    <span className="flex items-center gap-1.5 text-sm text-[#475569]"><Clock size={16} />{fmtDate(s.start_at, tz, { weekday: "short", year: undefined })} · {fmtTime(s.start_at, tz)} – {fmtTime(s.end_at, tz)}</span>
                  </div>
                ))}</div>}
              </div>
            </div>
            <div className="panel p-6">
              <div className="eyebrow">Venue</div>
              <div className="mt-3 font-semibold" data-testid="event-venue-panel">{e.venue?.name || "Venue being confirmed"}</div>
              <div className="text-sm text-[#475569]">{e.venue ? e.venue.address : "We'll email every registered attendee as soon as it's booked."}</div>
              <Link to={`/cities/${e.city?.slug}`} className="mt-3 inline-block text-sm font-semibold underline">More about {e.city?.name}</Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            {!e.registration?.open ? <ClosedPanel e={e} /> : e.seats_left === 0 ? <WaitlistForm eventId={e.id} citySlug={e.city?.slug} title="Sold out. Join the waitlist" /> : tiers.length === 0 ? (
              <div className="panel p-6" data-testid="tickets-not-on-sale"><div className="eyebrow">Tickets</div><p className="mt-2 text-sm">Prices are not set yet, so tickets are not on sale. Join the list and we'll tell you when they are.</p><div className="mt-4"><WaitlistForm eventId={e.id} citySlug={e.city?.slug} title="Notify me" /></div></div>
            ) : (
              <div className="panel sticky top-24 p-6" data-testid="ticket-selector">
                <div className="eyebrow">Choose your seat</div>
                <div className="mt-4"><PriceCards compact tiers={e.price_tiers} tz={tz} selected={selected} onSelect={setTier} tierLeft={e.tier_left} vipBenefits={e.vip_benefits} /></div>
                <p className="mt-3 text-xs text-[#475569]">Prices in US dollars, plus applicable taxes ({taxLabel}) added at checkout.</p>
                <div className="mt-4 rounded-md bg-[#F4F6FA] p-3 text-sm" data-testid="event-included"><div className="eyebrow mb-1">Included</div>Full-day class · Lunch (served) · Two coffee breaks · Course binder</div>
                <button className="btn-gold mt-5 w-full" data-testid="register-now-btn" onClick={() => nav(`/checkout?event=${e.slug}&tier=${selected}`)}>Register now</button>
                <RiskNotice className="mt-4" />
              </div>
            )}
          </div>
        </div>
      </Section>
    </>
  );
}
